import {
  BoldStyled,
  ExclamationTriangleIconStyled,
} from './top-ups-form-repartition.styled';
import { Checkbox, Stack, Tooltip, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import {
  initialTopUpsRepartition,
  topUpsFormRepartitionSchema,
} from './form-config';
import { useEffect, useMemo, useState } from 'react';

import { COLUMN_TABLE_REPARTITION_TOP_UPS } from './const';
import { JTable } from '@atoms/JTable';
import Jpaper from '@atoms/JPaper';
import RowRepartition from './row-repartition';
import TopUpsFooter from '../TopUpsFooter';
import TopUpsFormRepartitionData from '@/modules/credits/models/dto/top-ups-form-repartition-data';
import { useGetEstablishmentsWithThreshold } from '@establishment/queries/useGetEstablishment';
import useTopUpsMutationContext from '@/modules/credits/hooks/useTopUpsMutationContext';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';

const TopUpsFormRepartition = () => {
  const { t } = useTranslation();
  const { setStepIndex } = useTopUpsMutationContext();
  const { payload } = useTopUpsMutationContext();
  const [remainingCoins, setRemainingCoins] = useState<number>(0);
  const [repartitionIsBalanced, setRepartitionIsBalanced] =
    useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean>(true);

  const methods = useForm<TopUpsFormRepartitionData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: useMemo(() => {
      return payload?.creditSharingDtos
        ? { repartition: payload.creditSharingDtos }
        : initialTopUpsRepartition;
    }, [initialTopUpsRepartition]),
    resolver: yupResolver(topUpsFormRepartitionSchema(t)),
  });
  const { handleSubmit, getValues, setValue, trigger } = methods;
  const onSubmit = handleSubmit(() => {
    setStepIndex((prev) => (prev ? prev + 1 : 1));
  });

  const { data: establishments } = useGetEstablishmentsWithThreshold(
    payload?.createCreditTopupDto?.enterpriseId ?? '',
  );

  const checkFormRepartitionIsValid = (newRemaningCoins: number) => {
    if (
      newRemaningCoins === payload?.createCreditTopupDto?.creditsAmount ||
      newRemaningCoins === 0
    ) {
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  };

  useEffect(() => {
    if (!payload?.creditSharingDtos && establishments && establishments.data) {
      const repartitionInitial = establishments.data.map(({ id }) => {
        return { creditsAmount: 0, establishmentId: id };
      });

      setValue('repartition', repartitionInitial);
      trigger('repartition');
    }
    const remainingCoinsInitial = payload?.creditSharingDtos
      ? payload.creditSharingDtos.reduce(
          (accumulator, currentValue) =>
            accumulator - currentValue.creditsAmount,
          payload?.createCreditTopupDto?.creditsAmount ?? 0,
        )
      : payload?.createCreditTopupDto?.creditsAmount;
    setRemainingCoins(remainingCoinsInitial ?? 0);
    checkFormRepartitionIsValid(remainingCoinsInitial ?? 0);
  }, [establishments, payload]);

  const onChangeRepartition = (newRepartition: number, index: number) => {
    const repartition = getValues('repartition');

    const diffCoins = repartition[index].creditsAmount - newRepartition;
    repartition[index].creditsAmount = newRepartition;
    setValue('repartition', repartition);
    trigger('repartition');

    const newRemaningCoins = remainingCoins + diffCoins;

    setRemainingCoins(newRemaningCoins);
    checkFormRepartitionIsValid(newRemaningCoins);
    setRepartitionIsBalanced(false);
  };

  const onBalanceRepartition = () => {
    if (!establishments?.data || establishments?.data.length === 0) {
      return;
    }

    const numberOfEstablishment = establishments?.data.length;

    if (repartitionIsBalanced) {
      for (let index = 0; index < numberOfEstablishment; index++) {
        onChangeRepartition(0, index);
      }
      setRepartitionIsBalanced(false);
      setRemainingCoins(payload?.createCreditTopupDto?.creditsAmount ?? 0);
      setIsValid(true);

      return;
    }

    const jobCoins = payload?.createCreditTopupDto?.creditsAmount ?? 0;

    const remaining = jobCoins % numberOfEstablishment;

    const division = Math.floor(jobCoins / numberOfEstablishment);

    for (let index = 1; index < numberOfEstablishment; index++) {
      onChangeRepartition(division, index);
    }
    onChangeRepartition(division + remaining, 0);
    setRepartitionIsBalanced(true);
    setRemainingCoins(0);
    setIsValid(true);
  };

  const isErrorRemainingCoin = remainingCoins < 0;

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit} autoComplete="off" noValidate>
        <Stack gap={2}>
          <Jpaper gap={1} alignItems="stretch">
            <Stack direction={'row'} justifyContent={'flex-end'}>
              <Typography>{`${t('CREDIT_BALANCE')} : `}</Typography>

              {isErrorRemainingCoin && <ExclamationTriangleIconStyled />}
              <Tooltip
                title={isErrorRemainingCoin ? t('NOT_ENOUGH_CREDIT') : ''}
              >
                <BoldStyled attrs={{ error: isErrorRemainingCoin }}>
                  {remainingCoins}
                </BoldStyled>
              </Tooltip>
            </Stack>
            <JTable
              columns={COLUMN_TABLE_REPARTITION_TOP_UPS}
              withMinWidth={false}
            >
              {establishments?.data.map(
                ({ id, name, creditsAlertThreshold }, index) => {
                  const repartition = getValues('repartition');

                  const repartitionEtablishment = repartition
                    ? repartition[index].creditsAmount
                    : undefined;
                  return (
                    <RowRepartition
                      etablishmentName={name}
                      repartition={repartitionEtablishment}
                      key={id}
                      onChange={(value: number) =>
                        onChangeRepartition(value, index)
                      }
                      creditsAlertThreshold={creditsAlertThreshold}
                    />
                  );
                },
              )}
            </JTable>
            <Stack direction={'row'} alignItems={'center'}>
              <Checkbox
                onClick={onBalanceRepartition}
                checked={repartitionIsBalanced}
                data-qa="balance-repartition-checkbox"
              />
              <Typography>{t('BALANCE_REPARTITION')}</Typography>
            </Stack>
          </Jpaper>
          <TopUpsFooter
            isValid={isValid}
            values={
              remainingCoins === 0
                ? { creditSharingDtos: getValues('repartition') }
                : {}
            }
            submitTab={true}
          />
        </Stack>
      </form>
    </FormProvider>
  );
};

export default TopUpsFormRepartition;
